import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { TrelloMarkdown } from '@trello/markdown';
import { BusinessClassIcon } from '@trello/nachos/icons/business-class';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import type { TemplatesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { UpgradePromptButton } from 'app/src/components/UpgradePrompts';
import { TemplatesPowerUpTile } from './TemplatesPowerUpTile';
import type { Plugin } from './types';

import styles from './TemplatesPowerUp.less';

// eslint-disable-next-line @trello/no-module-logic
const markdown = new TrelloMarkdown();

const getDescription = (overview?: string, description?: string): string => {
  return markdown.text(overview || description || '').output;
};

interface TemplatesPowerUpProps {
  plugins: Plugin[];
  shouldShowBCUpgrade?: boolean;
}

export const TemplatesPowerUp: FunctionComponent<TemplatesPowerUpProps> = ({
  plugins,
  shouldShowBCUpgrade,
}) => {
  const trackBcUpgradeLink = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'bcUpgradeFromTemplatesPowerUpDescriptionLink',
      source: 'templateStoryPageScreen',
    });
  };

  return (
    <div
      className={styles.powerUpsContainer}
      data-testid={getTestId<TemplatesTestIds>('templates-power-ups-container')}
    >
      <div className={styles.powerUpsInner}>
        <h3 className={styles.powerUpsHeader}>
          <PowerUpIcon dangerous_className={styles.powerUpsHeaderIcon} />
          <FormattedMessage
            id="templates.templates.power-ups"
            defaultMessage="Power-Ups"
            description="The title of the section that displays power-ups"
          />
        </h3>
        <div className={styles.powerUpsDescription}>
          {shouldShowBCUpgrade ? (
            <FormattedMessage
              id="templates.templates.power-ups-section-upgrade-bc-copy"
              defaultMessage="Add features to your boards and integrate your favorite apps right into Trello. This template works best with {bcUpgradeLink} which allows you to use unlimited Power-Ups"
              description="An upsell message explaining the benefits of upgrading to use unlimited Power-Ups in Trello"
              values={{
                bcUpgradeLink: (
                  <a
                    href="/business-class"
                    target="_blank"
                    key={0}
                    onClick={trackBcUpgradeLink}
                  >
                    <FormattedMessage
                      id="templates.templates.business-class"
                      defaultMessage="Premium"
                      description="The title of the button that upgrades to Premium"
                    />
                  </a>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="templates.templates.power-ups-section-copy"
              defaultMessage="Add features to your boards and integrate your favorite apps right into Trello. Here are the Power-Ups enabled on this template:"
              description="The description of the power-ups section, which lists the Power-Ups enabled on this template"
            />
          )}
        </div>
        <div className={styles.powerUpsContainer}>
          {plugins?.map(({ icon, id, name, overview, description }) => (
            <TemplatesPowerUpTile
              key={id}
              icon={icon}
              name={name}
              description={getDescription(overview, description)}
            />
          ))}
        </div>
      </div>
      {shouldShowBCUpgrade && (
        <UpgradePromptButton
          theme="dark"
          cta={
            <FormattedMessage
              id="templates.templates.upgrade-to-business-class"
              defaultMessage="Upgrade to Premium"
              description="Button label for upgrade to Premium CTA"
            />
          }
          ctaLink="/business-class"
          openInNewTab
          ctaOnClick={() => {
            Analytics.sendClickedButtonEvent({
              buttonName: 'bcUpgradeFromTemplatesPowerUpButton',
              source: 'templateStoryPageScreen',
            });
          }}
          icon={<BusinessClassIcon />}
        />
      )}
    </div>
  );
};
